// Kick Sheets — well-control documentation & calculation support ONLY.
// Strict contract: never certifies kill mud weight / MAASP / well safety /
// well-control decisions, never gives operational instructions, never
// generates a choke or kill schedule, never infers kick location.
import { polishProse, MISSING } from "@/lib/ddr/templates";
import { pipeCapacity } from "@/lib/calc/drilling";
import { killMudWeight, initialCirculatingPressure, finalCirculatingPressure, pumpPressureSchedule } from "@/lib/calc/wellcontrol";

const orMissing = (v) => (v != null && String(v).trim() ? String(v).trim() : MISSING);
const polishedOrMissing = (v) => polishProse(v) || MISSING;
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
const sec = (lines, label) => lines.push(`\n=== ${label.toUpperCase()} ===\n`);
const CALC_LABEL = "Calculation support only — verify before operational use.";

// ---------------------------------------------------------------------------
// _buildStepDownRows — calls lib/calc/wellcontrol.pumpPressureSchedule with
// inputs derived from kick-sheet state. Parses the text schedule produced by
// the lib back into structured rows for printable output. Returns null when
// any required input is missing or a calc throws.
// All FORMULAS live in lib/calc/* — this is parse/glue only.
// ---------------------------------------------------------------------------
function _buildStepDownRows(state) {
  const g = state.geom || {}, p = state.pump || {}, sh = state.shutIn || {}, m = state.mud || {};
  const dpId  = num(g.dpId);
  const dpLen = num(g.dpLength);
  const sidpp = num(sh.sidpp);
  const scr   = num(p.slowPumpPressure);
  const omw   = num(m.currentMW);
  const tvd   = num(g.currentTvd);
  const pumpQ = num(p.pumpOutput);
  if ([dpId, dpLen, sidpp, scr, omw, tvd, pumpQ].some((v) => v == null)) return null;
  if (pumpQ <= 0) return null;
  try {
    const dsv = pipeCapacity({ id: dpId, length: dpLen }).result;       // bbl
    const totalStrokes = dsv / pumpQ;
    const icp = initialCirculatingPressure({ sidpp, scr }).result;
    const kmw = killMudWeight({ sidpp, tvd, currentMW: omw }).result;
    const fcp = finalCirculatingPressure({ scr, kmw, omw }).result;
    const sched = pumpPressureSchedule({
      icp, fcp, strokesToBit: totalStrokes, intervalStrokes: 100,
    });
    const rows = sched.steps
      .map((s) => s.match(/@\s*stk\s+(-?\d+)\s+→\s+(-?\d+)\s*psi/))
      .filter(Boolean)
      .map((m) => ({ strokes: parseInt(m[1], 10), pressure: parseInt(m[2], 10) }));
    return rows.length ? rows : null;
  } catch {
    return null;
  }
}


// ---------------------------------------------------------------------------
// Calculation-support helpers — all results returned only when ALL required
// inputs are present. Otherwise return null (no fabricated numbers).
// ---------------------------------------------------------------------------
export function kickCalcs(state) {
  const m = state.mud || {};
  const sh = state.shutIn || {};
  const g = state.geom || {};
  const p = state.pump || {};
  const mw = num(m.currentMW);
  const tvd = num(g.currentTvd);
  const sidpp = num(sh.sidpp);
  const sicp = num(sh.sicp);
  const slowP = num(p.slowPumpPressure);
  // Kill mud weight (ppg) = current MW + SIDPP / (0.052 × TVD)
  const killMW = (mw != null && sidpp != null && tvd && tvd > 0)
    ? Math.round((mw + sidpp / (0.052 * tvd)) * 1000) / 1000 : null;
  // Initial Circulating Pressure (ICP) = SIDPP + slow pump pressure
  const icp = (sidpp != null && slowP != null) ? Math.round((sidpp + slowP) * 10) / 10 : null;
  // Final Circulating Pressure (FCP) = slow pump pressure × (kill MW / current MW)
  const fcp = (slowP != null && killMW != null && mw && mw > 0)
    ? Math.round((slowP * (killMW / mw)) * 10) / 10 : null;
  return { killMW, icp, fcp, sicp };
}

const calcLine = (label, val) =>
  `${label}: ${val == null ? "Not computed (missing inputs)" : val}  [${CALC_LABEL}]`;

// ---------------------------------------------------------------------------
// Variant labels and main warning.
// ---------------------------------------------------------------------------
export const KICK_SHEET_WARNING =
  "RigSentinel Kick Sheets provide well-control documentation and calculation-support only. They do not provide live operational instructions, do not certify well-control decisions, and do not replace certified well-control personnel, approved procedures, company requirements, engineering review, or applicable regulations. All values must be verified by competent personnel before operational use.";

export const KICK_SHEET_QUICK_PHRASES = [
  "Values entered for review.",
  "Calculation support only.",
  "Verify against approved procedure.",
  "Review required before use.",
  "SIDPP entered as recorded.",
  "SICP entered as recorded.",
  "Pit gain entered as recorded.",
  "LOT / FIT reference entered.",
  "MAASP entered by user.",
  "No interpretation entered.",
  "Awaiting supervisor review.",
  "Awaiting well-control review.",
  "Pressure limits entered as recorded.",
  "Pump data entered as recorded.",
  "Mud data entered as recorded.",
];
export const KICK_SHEET_QUICK_PHRASES_SUBSEA = [
  "Riser volume entered.",
  "Choke line volume entered.",
  "Kill line volume entered.",
  "Subsea stack reference entered.",
  "Line friction entered as recorded.",
  "Subsea engineer review required.",
];

// Generated-section title labels per variant.
const VARIANT_TITLES = {
  surfaceDeviated:   "Surface Deviated Kick Data",
  surfaceHorizontal: "Surface Horizontal Kick Data",
  surfaceVertical:   "Surface Vertical Kick Data",
  subseaDeviated:    "Subsea Deviated Kick Data",
  subseaHorizontal:  "Subsea Horizontal Kick Data",
  subseaVertical:    "Subsea Vertical Kick Data",
};
export const VARIANT_FILENAMES = {
  surfaceDeviated:   "RigSentinel_Surface_Kick_Sheet_Deviated",
  surfaceHorizontal: "RigSentinel_Surface_Kick_Sheet_Horizontal",
  surfaceVertical:   "RigSentinel_Surface_Kick_Sheet_Vertical",
  subseaDeviated:    "RigSentinel_Subsea_Kick_Sheet_Deviated",
  subseaHorizontal:  "RigSentinel_Subsea_Kick_Sheet_Horizontal",
  subseaVertical:    "RigSentinel_Subsea_Kick_Sheet_Vertical",
};
export const VARIANT_DISPLAY = {
  surfaceDeviated:   "Surface Kick Sheet — Deviated Well",
  surfaceHorizontal: "Surface Kick Sheet — Horizontal Well",
  surfaceVertical:   "Surface Kick Sheet — Vertical Well",
  subseaDeviated:    "Subsea Kick Sheet — Deviated Well",
  subseaHorizontal:  "Subsea Kick Sheet — Horizontal Well",
  subseaVertical:    "Subsea Kick Sheet — Vertical Well",
};
const isSubsea = (v) => v === "subseaDeviated" || v === "subseaHorizontal" || v === "subseaVertical";
const isHorizontal = (v) => v === "surfaceHorizontal" || v === "subseaHorizontal";
const isVertical = (v) => v === "surfaceVertical" || v === "subseaVertical";
export const VARIANT_IS_VERTICAL = isVertical;
export const VARIANT_IS_SUBSEA = isSubsea;

// ---------------------------------------------------------------------------
// Main builder. variant ∈ surfaceDeviated | surfaceHorizontal | subseaDeviated | subseaHorizontal
// ---------------------------------------------------------------------------
export function buildKickSheet(variant, state) {
  const h = state.header || {};
  const g = state.geom || {};
  const m = state.mud || {};
  const sh = state.shutIn || {};
  const p = state.pump || {};
  const spec = state.spec || {};
  const subsea = state.subsea || {};
  const horiz = state.horiz || {};
  const review = state.review || {};
  const notes = state.notes || {};
  const lines = [];
  const push = (s) => lines.push(s);

  sec(lines, "Header / Well Information");
  push(`Date: ${orMissing(h.reportDate)}  ·  Report #: ${orMissing(h.reportNumber)}`);
  push(`Rig / Site: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Field / Block: ${orMissing(h.fieldBlock)}  ·  Operator / Client: ${orMissing(h.operator)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}  ·  Position: ${orMissing(h.position)}`);
  push(`Tour / Shift: ${orMissing(h.tour)}  ·  Unit System (user-entered): ${orMissing(h.unitSystem)}`);
  push(`Surface or Subsea: ${isSubsea(variant) ? "Subsea" : "Surface"}  ·  Profile: ${isVertical(variant) ? "Vertical" : (isHorizontal(variant) ? "Horizontal" : "Deviated")}`);
  push(`Well-Control Method Reference (user-entered): ${orMissing(h.wcMethod)}`);
  push(`Approved Procedure Reference: ${orMissing(h.procedureRef)}`);
  push(`Drilling Supervisor / Company Rep: ${orMissing(h.companyRep)}`);
  push(`Toolpusher / OIM: ${orMissing(h.toolpusher)}`);
  if (isSubsea(variant)) push(`Subsea Engineer: ${orMissing(h.subseaEngineer)}`);
  push(`Well-Control Supervisor: ${orMissing(h.wcSupervisor)}`);
  push(`Rough Notes: ${polishedOrMissing(h.roughNotes)}`);

  // Vertical variants: dedicated Stack / BOP Reference section (surface or subsea
  // flavour). Records references only — does NOT certify stack condition,
  // wellhead integrity, or control-system readiness.
  if (isVertical(variant)) {
    if (!isSubsea(variant)) {
      const sr = state.stackRef || {};
      sec(lines, "Surface Stack / BOP Reference");
      push(`Surface BOP Stack Reference: ${orMissing(sr.bopStackRef)}`);
      push(`BOP Test Sheet Reference: ${orMissing(sr.bopTestSheetRef)}  ·  Surface BOP Test Report Reference: ${orMissing(sr.surfaceBopReportRef)}`);
      push(`Stack Rated Working Pressure (user-entered): ${orMissing(sr.stackRwp)}`);
      push(`Wellhead / Casinghead Type: ${orMissing(sr.wellheadType)}  ·  Wellhead Pressure Rating: ${orMissing(sr.wellheadRating)}`);
      push(`Choke Manifold Reference: ${orMissing(sr.chokeManifoldRef)}  ·  Kill Line Reference: ${orMissing(sr.killLineRef)}`);
      push(`Accumulator / Closing Unit Reference: ${orMissing(sr.accumulatorRef)}`);
      push(`Test Procedure Reference: ${orMissing(sr.testProcedureRef)}`);
      push(`Rough Notes: ${polishedOrMissing(sr.roughNotes)}`);
      push("");
      push("Stack and wellhead references recorded as user-entered only. Tool does not certify stack condition, wellhead integrity, choke manifold readiness, or accumulator capacity.");
    } else {
      const sr = state.stackRef || {};
      sec(lines, "Subsea Stack / BOP Reference");
      push(`Subsea BOP Stack Reference: ${orMissing(sr.bopStackRef)}  ·  Subsea BOP Test Report Reference: ${orMissing(sr.subseaBopReportRef)}`);
      push(`Stack Serial Number / ID: ${orMissing(sr.stackSerial)}  ·  Stack Configuration: ${orMissing(sr.stackConfig)}`);
      push(`LMRP Reference: ${orMissing(sr.lmrpRef)}  ·  Wellhead Connector Reference: ${orMissing(sr.wellheadConnectorRef)}`);
      push(`Subsea Stack Depth: ${orMissing(sr.subseaStackDepth)}  ·  Water Depth: ${orMissing(sr.waterDepth)}`);
      push(`RKB → Sea Level: ${orMissing(sr.rkbToSea)}  ·  Mudline Depth: ${orMissing(sr.mudlineDepth)}`);
      push(`Choke Manifold Reference: ${orMissing(sr.chokeManifoldRef)}  ·  Control Pod Reference: ${orMissing(sr.controlPodRef)}`);
      push(`ROV Intervention Reference: ${orMissing(sr.rovRef)}  ·  Test Procedure Reference: ${orMissing(sr.testProcedureRef)}`);
      push(`Rough Notes: ${polishedOrMissing(sr.roughNotes)}`);
      push("");
      push("Subsea stack, LMRP, control pod, and ROV references recorded as user-entered only. Tool does not certify subsea stack condition, control-system readiness, or ROV-intervention readiness.");
    }
  }

  sec(lines, "Well Geometry");
  push(`Current MD: ${orMissing(g.currentMd)}  ·  Current TVD: ${orMissing(g.currentTvd)}`);
  push(`Shoe MD: ${orMissing(g.shoeMd)}  ·  Shoe TVD: ${orMissing(g.shoeTvd)}`);
  push(`Hole Size: ${orMissing(g.holeSize)}  ·  Casing Size: ${orMissing(g.casingSize)}  ·  Casing ID: ${orMissing(g.casingId)}`);
  push(`Drill Pipe OD: ${orMissing(g.dpOd)}  ·  Drill Pipe ID: ${orMissing(g.dpId)}`);
  push(`HWDP OD: ${orMissing(g.hwdpOd)}  ·  HWDP ID: ${orMissing(g.hwdpId)}`);
  push(`Drill Collar OD: ${orMissing(g.dcOd)}  ·  Drill Collar ID: ${orMissing(g.dcId)}`);
  push(`BHA Length: ${orMissing(g.bhaLength)}  ·  Open Hole Length: ${orMissing(g.openHoleLength)}`);
  if (isHorizontal(variant)) {
    push(`Horizontal Section Length: ${orMissing(g.horizLength)}`);
  }
  push(`Build / Tangent / Lateral Notes: ${polishedOrMissing(g.profileNotes)}`);
  push(`Directional Survey Reference: ${orMissing(g.surveyRef)}`);
  push(`Rough Notes: ${polishedOrMissing(g.roughNotes)}`);

  sec(lines, "Mud and Fluid Data");
  push(`Current Mud Weight: ${orMissing(m.currentMW)}  ·  Original Mud Weight: ${orMissing(m.originalMW)}`);
  push(`Mud Type: ${orMissing(m.mudType)}  ·  Mud Gradient: ${orMissing(m.mudGradient)}`);
  push(`PV: ${orMissing(m.pv)}  ·  YP: ${orMissing(m.yp)}  ·  Gels: ${orMissing(m.gels)}`);
  push(`Chlorides / Salinity: ${orMissing(m.chlorides)}  ·  Temperature Notes: ${polishedOrMissing(m.tempNotes)}`);
  push(`Mud Report Reference: ${orMissing(m.mudReportRef)}`);
  push(`Rough Notes: ${polishedOrMissing(m.roughNotes)}`);

  sec(lines, "Shut-In Data");
  push(`Time Kick Observed (user-entered): ${orMissing(sh.timeObserved)}`);
  push(`Time Shut In (user-entered): ${orMissing(sh.timeShutIn)}`);
  push(`SIDPP (user-entered): ${orMissing(sh.sidpp)}  ·  SICP (user-entered): ${orMissing(sh.sicp)}`);
  push(`Shut-In Drill Pipe Pressure (user-entered): ${orMissing(sh.sidp)}`);
  push(`Shut-In Casing Pressure (user-entered): ${orMissing(sh.sicpAlt)}`);
  push(`Pit Gain (user-entered): ${orMissing(sh.pitGain)}`);
  push(`Flow Check Result (user-entered text only): ${polishedOrMissing(sh.flowCheck)}`);
  push(`Pump Status: ${orMissing(sh.pumpStatus)}  ·  Choke Status (user-entered): ${orMissing(sh.chokeStatus)}`);
  push(`Well Status (user-entered): ${orMissing(sh.wellStatus)}`);
  push(`Rough Notes: ${polishedOrMissing(sh.roughNotes)}`);
  push("");
  push("Shut-in values recorded as user-entered observations only. Tool does not certify shut-in, does not interpret the flow-check result, and does not declare the well static or safe.");

  sec(lines, "Pump / Circulation Data");
  push(`Pump #: ${orMissing(p.pumpNo)}  ·  Liner Size: ${orMissing(p.linerSize)}  ·  Stroke Length: ${orMissing(p.strokeLength)}`);
  push(`Pump Efficiency: ${orMissing(p.efficiency)}  ·  Pump Output (user-entered): ${orMissing(p.pumpOutput)}`);
  push(`Slow Pump Rate (user-entered): ${orMissing(p.slowPumpRate)}  ·  Slow Pump Pressure (user-entered): ${orMissing(p.slowPumpPressure)}`);
  push(`SPM: ${orMissing(p.spm)}  ·  Stroke Counter: ${orMissing(p.strokeCounter)}`);
  push(`Surface-to-Bit Strokes (user-entered): ${orMissing(p.strokesSurfToBit)}`);
  push(`Bit-to-Shoe Strokes (user-entered): ${orMissing(p.strokesBitToShoe)}`);
  push(`Total Circulating Strokes (user-entered): ${orMissing(p.strokesTotal)}`);
  push(`Rough Notes: ${polishedOrMissing(p.roughNotes)}`);

  // Variant-specific kick data
  sec(lines, VARIANT_TITLES[variant]);
  if (variant === "surfaceDeviated") {
    push(`TVD at Bit: ${orMissing(spec.tvdAtBit)}  ·  MD at Bit: ${orMissing(spec.mdAtBit)}`);
    push(`Drill String Capacity (user-entered): ${orMissing(spec.dsCap)}`);
    push(`Annular Capacity (user-entered): ${orMissing(spec.annCap)}`);
    push(`Open-Hole Annular Capacity: ${orMissing(spec.openHoleAnnCap)}`);
    push(`Casing Annular Capacity: ${orMissing(spec.casingAnnCap)}`);
    push(`Kick Tolerance Reference: ${orMissing(spec.kickToleranceRef)}`);
    push(`MAASP (user-entered): ${orMissing(spec.maasp)}`);
    push(`Maximum Casing Pressure (user-entered): ${orMissing(spec.maxCsgP)}`);
    push(`LOT / FIT Reference: ${orMissing(spec.lotFitRef)}`);
    push(`Initial Circulating Pressure (user-entered or calc-support): ${orMissing(spec.icpUser)}`);
    push(`Final Circulating Pressure (user-entered or calc-support): ${orMissing(spec.fcpUser)}`);
    push(`Kill Mud Weight (user-entered or calc-support): ${orMissing(spec.killMwUser)}`);
  } else if (variant === "surfaceHorizontal") {
    push(`Vertical Section TVD: ${orMissing(horiz.vertTvd)}  ·  Lateral Length: ${orMissing(horiz.lateralLength)}`);
    push(`Kick Location Assumption (user-entered ONLY — not inferred by tool): ${orMissing(horiz.kickLocAssumption)}`);
    push(`Horizontal Annular Volume (user-entered): ${orMissing(horiz.horizAnnVol)}`);
    push(`Drill String Volume (user-entered): ${orMissing(horiz.dsVol)}`);
    push(`Annular Volume (user-entered): ${orMissing(horiz.annVol)}`);
    push(`Lag Strokes (user-entered): ${orMissing(horiz.lagStrokes)}`);
    push(`Pump Strokes to Bit (user-entered): ${orMissing(horiz.strokesToBit)}`);
    push(`Pump Strokes to Shoe (user-entered): ${orMissing(horiz.strokesToShoe)}`);
    push(`Pump Strokes Through Lateral (user-entered): ${orMissing(horiz.strokesThroughLateral)}`);
    push(`MAASP (user-entered): ${orMissing(spec.maasp)}`);
    push(`LOT / FIT Reference: ${orMissing(spec.lotFitRef)}`);
    push("");
    push("Tool does not infer kick location. No kill sequence, choke schedule, or operational commands are generated.");
  } else if (variant === "subseaDeviated") {
    push(`Water Depth: ${orMissing(subsea.waterDepth)}  ·  RKB → Sea Level: ${orMissing(subsea.rkbToSea)}`);
    push(`Riser Length: ${orMissing(subsea.riserLength)}  ·  Riser ID: ${orMissing(subsea.riserId)}  ·  Riser Volume: ${orMissing(subsea.riserVol)}`);
    push(`Choke Line ID: ${orMissing(subsea.chokeId)}  ·  Choke Line Length: ${orMissing(subsea.chokeLength)}  ·  Choke Line Volume: ${orMissing(subsea.chokeVol)}`);
    push(`Kill Line ID: ${orMissing(subsea.killId)}  ·  Kill Line Length: ${orMissing(subsea.killLength)}  ·  Kill Line Volume: ${orMissing(subsea.killVol)}`);
    push(`Subsea BOP Stack Depth: ${orMissing(subsea.stackDepth)}`);
    push(`Choke Line Friction Pressure (user-entered): ${orMissing(subsea.chokeFriction)}`);
    push(`Kill Line Friction Pressure (user-entered): ${orMissing(subsea.killFriction)}`);
    push(`Subsea Stack Reference: ${orMissing(subsea.stackRef)}`);
    push(`LOT / FIT Reference: ${orMissing(spec.lotFitRef)}  ·  MAASP (user-entered): ${orMissing(spec.maasp)}`);
    push("");
    push("Subsea line-friction values are recorded as user-entered only. Tool does not automatically correct pressures for subsea line friction and does not certify subsea well-control decisions.");
  } else if (variant === "subseaHorizontal") {
    push(`Water Depth: ${orMissing(subsea.waterDepth)}  ·  Riser Length: ${orMissing(subsea.riserLength)}  ·  Riser Volume: ${orMissing(subsea.riserVol)}`);
    push(`Choke Line Volume: ${orMissing(subsea.chokeVol)}  ·  Kill Line Volume: ${orMissing(subsea.killVol)}`);
    push(`Subsea Stack Depth: ${orMissing(subsea.stackDepth)}`);
    push(`Lateral Length: ${orMissing(horiz.lateralLength)}  ·  Horizontal Section Volume (user-entered): ${orMissing(horiz.horizAnnVol)}`);
    push(`Kick Location Assumption (user-entered ONLY — not inferred by tool): ${orMissing(horiz.kickLocAssumption)}`);
    push(`Pump Strokes to Bit (user-entered): ${orMissing(horiz.strokesToBit)}`);
    push(`Pump Strokes to Shoe (user-entered): ${orMissing(horiz.strokesToShoe)}`);
    push(`Pump Strokes Through Lateral (user-entered): ${orMissing(horiz.strokesThroughLateral)}`);
    push(`Choke Line Friction Pressure (user-entered): ${orMissing(subsea.chokeFriction)}`);
    push(`Kill Line Friction Pressure (user-entered): ${orMissing(subsea.killFriction)}`);
    push(`LOT / FIT Reference: ${orMissing(spec.lotFitRef)}  ·  MAASP (user-entered): ${orMissing(spec.maasp)}`);
    push("");
    push("Tool does not infer kick location. No operational commands, kill sequences, choke schedules, or pump-rate instructions are produced. Subsea line-friction values recorded as user-entered only; no automatic pressure correction.");
  } else if (variant === "surfaceVertical") {
    push(`TVD at Bit: ${orMissing(spec.tvdAtBit)}  ·  MD at Bit: ${orMissing(spec.mdAtBit)}`);
    push(`Drill String Capacity (user-entered): ${orMissing(spec.dsCap)}`);
    push(`Annular Capacity (user-entered): ${orMissing(spec.annCap)}`);
    push(`Open-Hole Annular Capacity: ${orMissing(spec.openHoleAnnCap)}`);
    push(`Casing Annular Capacity: ${orMissing(spec.casingAnnCap)}`);
    push(`Total System Volume (user-entered): ${orMissing(spec.totalSysVol)}`);
    push(`Surface-to-Bit Strokes (user-entered): ${orMissing(p.strokesSurfToBit)}`);
    push(`Bit-to-Shoe Strokes (user-entered): ${orMissing(p.strokesBitToShoe)}`);
    push(`Total Circulating Strokes (user-entered): ${orMissing(p.strokesTotal)}`);
    push(`Circulation Rate (user-entered): ${orMissing(spec.circRate)}  ·  Circulation Time (user-entered): ${orMissing(spec.circTime)}`);
    push("");
    push("Vertical-well surface kick data recorded as user-entered only. No lateral, build, or kick-location-in-lateral fields are required for vertical wells. No operational commands, kill sequences, choke schedules, or pump-rate instructions are produced.");
  } else if (variant === "subseaVertical") {
    push(`Water Depth: ${orMissing(subsea.waterDepth)}  ·  RKB → Sea Level: ${orMissing(subsea.rkbToSea)}`);
    push(`Riser Length: ${orMissing(subsea.riserLength)}  ·  Riser ID: ${orMissing(subsea.riserId)}  ·  Riser Volume: ${orMissing(subsea.riserVol)}  ·  Riser Capacity: ${orMissing(subsea.riserCap)}`);
    push(`Choke Line ID: ${orMissing(subsea.chokeId)}  ·  Choke Line Length: ${orMissing(subsea.chokeLength)}  ·  Choke Line Volume: ${orMissing(subsea.chokeVol)}  ·  Choke Line Capacity: ${orMissing(subsea.chokeCap)}`);
    push(`Kill Line ID: ${orMissing(subsea.killId)}  ·  Kill Line Length: ${orMissing(subsea.killLength)}  ·  Kill Line Volume: ${orMissing(subsea.killVol)}  ·  Kill Line Capacity: ${orMissing(subsea.killCap)}`);
    push(`Choke Line Friction Pressure (user-entered): ${orMissing(subsea.chokeFriction)}  ·  Kill Line Friction Pressure (user-entered): ${orMissing(subsea.killFriction)}`);
    push(`Boost Line Reference: ${orMissing(subsea.boostLineRef)}  ·  Auxiliary Line Reference: ${orMissing(subsea.auxLineRef)}`);
    push(`Subsea Stack Depth: ${orMissing(subsea.stackDepth)}`);
    push(`Surface-to-Bit Strokes (user-entered): ${orMissing(p.strokesSurfToBit)}`);
    push(`Bit-to-Shoe Strokes (user-entered): ${orMissing(p.strokesBitToShoe)}`);
    push(`Riser Strokes (user-entered): ${orMissing(p.strokesRiser)}  ·  Choke Line Strokes (user-entered): ${orMissing(p.strokesChoke)}  ·  Kill Line Strokes (user-entered): ${orMissing(p.strokesKill)}`);
    push(`Total Circulating Strokes (user-entered): ${orMissing(p.strokesTotal)}`);
    push(`Circulation Rate (user-entered): ${orMissing(spec.circRate)}  ·  Circulation Time (user-entered): ${orMissing(spec.circTime)}`);
    push("");
    push("Vertical-well subsea kick data recorded as user-entered only. No lateral, build, or kick-location-in-lateral fields are required for vertical wells. Subsea line-friction values recorded as user-entered only; tool does not automatically correct pressures for subsea line friction. No operational commands, kill sequences, choke schedules, or pump-rate instructions are produced.");
  }

  // Vertical variants: Pressure Limits / LOT-FIT reference section. Records
  // user-entered limits only — never certifies MAASP, shoe integrity, casing
  // pressure limits, or operational acceptance.
  if (isVertical(variant)) {
    const lim = state.limits || {};
    sec(lines, "Pressure Limits / LOT-FIT Reference");
    push(`LOT / FIT Report Reference: ${orMissing(lim.lotFitReportRef)}  ·  LOT / FIT Test Depth TVD: ${orMissing(lim.lotFitDepthTvd)}`);
    push(`LOT / FIT Pressure (user-entered): ${orMissing(lim.lotFitPressure)}  ·  LOT / FIT Mud Weight (user-entered): ${orMissing(lim.lotFitMw)}`);
    push(`MAASP (user-entered): ${orMissing(lim.maasp)}  ·  Maximum Casing Pressure (user-entered): ${orMissing(lim.maxCsgP)}`);
    if (isSubsea(variant)) {
      push(`Subsea Pressure Limitations (user-entered): ${orMissing(lim.subseaLimits)}`);
    }
    push(`Shoe Integrity Notes (user-entered): ${polishedOrMissing(lim.shoeIntegrity)}`);
    push(`Operator Limit Reference: ${orMissing(lim.operatorLimitRef)}  ·  Client Limit Reference: ${orMissing(lim.clientLimitRef)}`);
    push(`Rough Notes: ${polishedOrMissing(lim.roughNotes)}`);
    push("");
    push("Pressure limits and LOT / FIT values recorded as user-entered only. Tool does not certify MAASP, shoe integrity, casing pressure limits, formation strength, or operational acceptance. Limits must be reviewed under company / regulatory requirements before operational use.");
  }

  sec(lines, "Calculation Support Values");
  const c = kickCalcs(state);
  push(calcLine("Kill Mud Weight (ppg, calc-support)", c.killMW));
  push(calcLine("Initial Circulating Pressure (psi, calc-support)", c.icp));
  push(calcLine("Final Circulating Pressure (psi, calc-support)", c.fcp));
  push("");
  push("Calculated values derive ONLY from user-entered inputs. Tool does not certify kill mud weight, ICP, FCP, MAASP, casing pressure limits, shoe integrity, or any well-control decision. All values must be reviewed by competent personnel before operational use. No operational commands, kill sequences, choke schedules, or pump-rate instructions are produced.");

  sec(lines, "Review / Approval Placeholders");
  push(`Prepared By: ${orMissing(review.preparedBy)}  ·  Checked By: ${orMissing(review.checkedBy)}`);
  push(`Toolpusher: ${orMissing(review.toolpusher)}  ·  Drilling Supervisor: ${orMissing(review.drillingSupervisor)}`);
  push(`Well-Control Supervisor: ${orMissing(review.wcSupervisor)}`);
  push(`Company Representative: ${orMissing(review.companyRep)}  ·  Client Representative: ${orMissing(review.clientRep)}`);
  push(`Date / Time Reviewed: ${orMissing(review.reviewedAt)}`);
  push(`Comments: ${polishedOrMissing(review.comments)}`);
  push("");
  push("Approval is not implied unless the user records reviewed / approved status. Signatures are recorded outside this tool.");

  if (notes.roughNotes) {
    sec(lines, "Additional Rough Notes");
    push(polishedOrMissing(notes.roughNotes));
  }

  // Field Data Notes — actual tally / measurement overrides captured by the
  // crew. Appears only when the operator entered something; otherwise omitted
  // entirely to keep the report tight.
  const fdNotes = (state.fieldData && state.fieldData.notes) || "";
  if (fdNotes.trim()) {
    sec(lines, "Field Data Notes (actual measurements / tally overrides)");
    push(polishedOrMissing(fdNotes));
  }

  // Step-down schedule — only emitted when every required input is present.
  // Formulas imported via the calc lib at render time; this builder string-only.
  const schedRows = _buildStepDownRows(state);
  if (schedRows) {
    sec(lines, "Step-Down Pump Pressure Schedule (calc support · 100-stk intervals)");
    push("Schedule is calculation support only. Verify with well-control supervisor before use.");
    push("");
    push("  Strokes   →   Pressure (psi)");
    schedRows.forEach((r) => push(`  ${String(r.strokes).padStart(7, " ")}   →   ${r.pressure}`));
  }

  sec(lines, "Kick Sheet Global Warning");
  push(KICK_SHEET_WARNING);

  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Checklist — returns array of missing-field warnings.
// ---------------------------------------------------------------------------
export function kickSheetChecklist(variant, s) {
  const out = [];
  const h = s.header || {}, g = s.geom || {}, m = s.mud || {}, sh = s.shutIn || {}, p = s.pump || {}, spec = s.spec || {}, subsea = s.subsea || {}, horiz = s.horiz || {}, review = s.review || {}, stackRef = s.stackRef || {}, lim = s.limits || {};
  if (!h.reportDate) out.push("Date missing");
  if (!h.rigName) out.push("Rig / site missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.procedureRef) out.push("Approved procedure reference missing");
  if (!g.currentMd) out.push("Current MD missing");
  if (!g.currentTvd) out.push("Current TVD missing");
  if (!g.shoeTvd) out.push("Shoe TVD missing");
  if (!m.currentMW) out.push("Current mud weight missing");
  if (!sh.sidpp) out.push("SIDPP missing");
  if (!sh.sicp) out.push("SICP missing");
  if (!sh.pitGain) out.push("Pit gain missing");
  if (!p.slowPumpRate) out.push("Slow pump rate missing");
  if (!p.slowPumpPressure) out.push("Slow pump pressure missing");
  // For vertical variants the LOT / FIT + MAASP live under the `limits` group;
  // for deviated / horizontal they live under `spec`. Accept either.
  const lotFit = lim.lotFitReportRef || spec.lotFitRef;
  const maasp = lim.maasp || spec.maasp;
  if (!lotFit) out.push("LOT / FIT reference missing");
  if (!maasp) out.push("MAASP missing");
  if (!review.preparedBy && !review.toolpusher && !review.companyRep && !review.wcSupervisor) out.push("Review personnel missing");
  if (isSubsea(variant)) {
    if (!subsea.waterDepth && !stackRef.waterDepth) out.push("Water depth missing");
    if (!subsea.riserVol && !subsea.chokeVol && !subsea.killVol) out.push("Riser / choke / kill line data missing");
  }
  if (isHorizontal(variant)) {
    if (!horiz.lateralLength && !g.horizLength) out.push("Lateral / horizontal section data missing");
  }
  if (isVertical(variant)) {
    if (!isSubsea(variant)) {
      if (!stackRef.bopStackRef && !stackRef.surfaceBopReportRef && !stackRef.bopTestSheetRef) out.push("Surface stack / BOP reference missing");
      if (!stackRef.wellheadType && !stackRef.wellheadRating) out.push("Wellhead / casinghead reference missing");
      if (!stackRef.chokeManifoldRef) out.push("Choke manifold reference missing");
    } else {
      if (!subsea.riserVol && !subsea.riserLength && !subsea.riserId) out.push("Riser data missing");
      if (!subsea.chokeVol && !subsea.chokeLength && !subsea.chokeId) out.push("Choke line data missing");
      if (!subsea.killVol && !subsea.killLength && !subsea.killId) out.push("Kill line data missing");
      if (!stackRef.bopStackRef && !stackRef.subseaBopReportRef && !stackRef.stackSerial) out.push("Subsea stack reference missing");
      if (!h.subseaEngineer && !review.subseaEngineer) out.push("Subsea engineer review missing");
    }
  }
  return out;
}
